import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  hideMindMapBranch,
  listModeratedBranches,
  restoreMindMapBranch,
} from '../../utils/mindmap/apiClient';
import {
  parseNodeBranchModerationChangedEvent,
} from '../../utils/mindmap/nodeBranchModeration';

function cacheKey(mindmapId) {
  return `mindmappi:branch-moderation:v1:${mindmapId}`;
}

function readCachedProjection(mindmapId) {
  if (!mindmapId || typeof window === 'undefined') return { found: false, branches: [] };
  try {
    const raw = window.localStorage.getItem(cacheKey(mindmapId));
    if (raw === null) return { found: false, branches: [] };
    const parsed = JSON.parse(raw);
    return { found: Array.isArray(parsed), branches: Array.isArray(parsed) ? parsed : [] };
  } catch {
    return { found: false, branches: [] };
  }
}

function writeCachedBranches(mindmapId, branches) {
  if (!mindmapId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(cacheKey(mindmapId), JSON.stringify(branches));
  } catch {
    // The live in-memory projection remains authoritative for this session.
  }
}

export function useNodeBranchModeration({ mindmapId, enabled, provider }) {
  const inputIdentity = `${enabled ? 'enabled' : 'disabled'}:${mindmapId || ''}`;
  const initialCacheRef = useRef(null);
  if (initialCacheRef.current === null) {
    initialCacheRef.current = readCachedProjection(mindmapId);
  }
  const initialCache = initialCacheRef.current;
  const transitionCache = useMemo(() => readCachedProjection(mindmapId), [mindmapId]);
  const [branches, setBranches] = useState(() => initialCache.branches);
  const [isReady, setIsReady] = useState(() => !enabled || initialCache.found);
  const [projectionIdentity, setProjectionIdentity] = useState(inputIdentity);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const requestVersionRef = useRef(0);
  const mutationVersionRef = useRef(0);
  const mutationInFlightRef = useRef(false);
  const identityRef = useRef({ mindmapId, enabled });
  identityRef.current = { mindmapId, enabled };

  useEffect(() => {
    const cached = readCachedProjection(mindmapId);
    requestVersionRef.current += 1;
    mutationVersionRef.current += 1;
    mutationInFlightRef.current = false;
    setIsUpdating(false);
    setProjectionIdentity(inputIdentity);
    setBranches(cached.branches);
    setIsReady(!enabled || cached.found);
    setError(null);
  }, [enabled, inputIdentity, mindmapId]);

  const refresh = useCallback(async () => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    if (!enabled || !mindmapId) {
      if (requestVersionRef.current === requestVersion) {
        setProjectionIdentity(inputIdentity);
        setBranches([]);
        setIsReady(true);
      }
      return [];
    }
    try {
      const payload = await listModeratedBranches(mindmapId);
      if (
        requestVersionRef.current !== requestVersion
        || identityRef.current.mindmapId !== mindmapId
        || !identityRef.current.enabled
      ) return null;
      const next = Array.isArray(payload?.branches) ? payload.branches : [];
      setProjectionIdentity(inputIdentity);
      setBranches(next);
      setIsReady(true);
      writeCachedBranches(mindmapId, next);
      setError(null);
      return next;
    } catch (nextError) {
      // Preserve the last successful/cached projection while offline.
      if (
        requestVersionRef.current === requestVersion
        && identityRef.current.mindmapId === mindmapId
      ) setError(nextError);
      return null;
    }
  }, [enabled, inputIdentity, mindmapId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const refreshOnFocus = () => void refresh();
    window.addEventListener('focus', refreshOnFocus);
    window.addEventListener('online', refreshOnFocus);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      window.removeEventListener('online', refreshOnFocus);
    };
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || !provider?.on || !provider?.off) return undefined;
    let timer = null;
    const scheduleRefresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void refresh(), 100);
    };
    const onStateless = ({ payload } = {}) => {
      if (parseNodeBranchModerationChangedEvent(payload, mindmapId)) scheduleRefresh();
    };
    const onStatus = ({ status } = {}) => {
      if (status === 'connected') scheduleRefresh();
    };
    provider.on('stateless', onStateless);
    provider.on('status', onStatus);
    return () => {
      if (timer) clearTimeout(timer);
      provider.off('stateless', onStateless);
      provider.off('status', onStatus);
    };
  }, [enabled, mindmapId, provider, refresh]);

  const hide = useCallback(async (nodeId, creatorUserId) => {
    if (!enabled || !mindmapId || mutationInFlightRef.current) return false;
    const mutationVersion = mutationVersionRef.current + 1;
    mutationVersionRef.current = mutationVersion;
    mutationInFlightRef.current = true;
    setIsUpdating(true);
    setError(null);
    try {
      await hideMindMapBranch(mindmapId, nodeId, creatorUserId);
      await refresh();
      return true;
    } catch (nextError) {
      if (
        mutationVersionRef.current === mutationVersion
        && identityRef.current.mindmapId === mindmapId
      ) setError(nextError);
      return false;
    } finally {
      if (mutationVersionRef.current === mutationVersion) {
        mutationInFlightRef.current = false;
        if (identityRef.current.mindmapId === mindmapId) setIsUpdating(false);
      }
    }
  }, [enabled, mindmapId, refresh]);

  const restore = useCallback(async (nodeId) => {
    if (!enabled || !mindmapId || mutationInFlightRef.current) return false;
    const mutationVersion = mutationVersionRef.current + 1;
    mutationVersionRef.current = mutationVersion;
    mutationInFlightRef.current = true;
    setIsUpdating(true);
    setError(null);
    try {
      await restoreMindMapBranch(mindmapId, nodeId);
      await refresh();
      return true;
    } catch (nextError) {
      if (
        mutationVersionRef.current === mutationVersion
        && identityRef.current.mindmapId === mindmapId
      ) setError(nextError);
      return false;
    } finally {
      if (mutationVersionRef.current === mutationVersion) {
        mutationInFlightRef.current = false;
        if (identityRef.current.mindmapId === mindmapId) setIsUpdating(false);
      }
    }
  }, [enabled, mindmapId, refresh]);

  const visibleBranches = projectionIdentity === inputIdentity
    ? branches
    : transitionCache.branches;
  const visibleIsReady = projectionIdentity === inputIdentity
    ? isReady
    : (!enabled || transitionCache.found);

  return useMemo(() => ({
    branches: visibleBranches,
    branchByNodeId: new Map(visibleBranches.map((branch) => [branch.nodeId, branch])),
    isUpdating: projectionIdentity === inputIdentity ? isUpdating : false,
    isReady: visibleIsReady,
    error,
    hide,
    restore,
    refresh,
  }), [
    error,
    hide,
    inputIdentity,
    isUpdating,
    projectionIdentity,
    refresh,
    restore,
    visibleBranches,
    visibleIsReady,
  ]);
}
